/** Catalog sync + Booking backfill — the data steps: the sync pages' own forms,
 *  embedded with the tenant's scope locked, launched THROUGH the preflight item.
 *
 *  Submit stages the form's params on the item context (`stage/`, deep-merge)
 *  and calls the item's `execute/` — so the ITEM owns the background task
 *  (`context.bgtask`), the 60s poller completes it, and the armed setup run
 *  cascades dependents (catalog → backfill). While the task runs the form gives
 *  way to a TaskProgressCard; afterwards the result line stays and the form can
 *  be reopened for another run. The same launch is also reachable later from
 *  the standalone /sync pages — this step is the setup-flavored host.
 *
 *  Catalog sync on an integration that keeps catalogue rows (a settings registry —
 *  a new Ventrata tenant with `catalogue_sync` on) embeds the catalogue section the
 *  product-sync form mounts by itself: products are chosen, configured and launched
 *  there, and its launch stages the included (or ticked, or matching) products' ids —
 *  at most 500 — as `sync_params.product_ids`. The step only names that mode; the
 *  registry query is the form's own, so asking it here costs no request. Any other
 *  integration keeps the plain listing and its "Map selected as…".
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { items as itemsApi, type PreflightItem } from '@/api/preflight';
import type { SyncBookingsStartInput, SyncOrdersStartInput, SyncProductsStartInput } from '@/api/ops';
import * as syncApi from '@/api/sync';
import type { SyncArgChoice } from '@/api/sync';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';
import { BookingSyncForm } from '@/features/sync/BookingSyncForm';
import { CATALOGUE_STORY } from '@/features/sync/catalogue/help';
import { catalogueScopeOf, useCatalogueRegistry } from '@/features/sync/catalogue/queries';
import { OrderSyncForm } from '@/features/sync/OrderSyncForm';
import { ProductSyncForm } from '@/features/sync/ProductSyncForm';
import type { SyncScope } from '@/features/sync/SyncScopePicker';

import { PreflightItemRunLogDrawer } from '../../tabs/PreflightRunLog';
import type { PreflightItemActions } from '../../tabs/usePreflightItemActions';
import { taskIdOf } from '../SetupItemPanel';

export interface IntegrationOption {
  integration: string;
  accounts: Array<{ slug: string | null; display_name: string; is_default: boolean }>;
  features: string[];
}

interface SyncDetail {
  integration_options?: IntegrationOption[];
  sync_params?: Record<string, unknown>;
  mode?: 'orders' | 'bookings';
  synced_product_ids?: string[];
}

export function useItemLaunch(topId: number, item: PreflightItem, actions: PreflightItemActions) {
  /** stage + execute → the spawned task id (the run's external_refs carry it). */
  return async (params: Record<string, unknown>): Promise<{ id: string }> => {
    const syncParams = { ...params };
    delete syncParams.tour_operator_id; // the item's tenant is authoritative
    await itemsApi.stage(topId, item.id, { sync_params: syncParams });
    const resp = await itemsApi.execute(topId, item.id);
    const run = resp.run;
    if (!run || run.status === 'error') {
      actions.invalidate();
      throw new Error(run?.error_message || 'The sync did not start — see the item log.');
    }
    const refs = (run.result?.external_refs ?? {}) as { bgtask_id?: string };
    actions.invalidate();
    if (!refs.bgtask_id) throw new Error('The run did not report a task id — see the item log.');
    return { id: refs.bgtask_id };
  };
}

function ScopeChoice({
  options,
  requiredFeatures,
  value,
  onChange,
  companyOf,
}: {
  options: IntegrationOption[];
  requiredFeatures: string[];
  value: { integration: string; account: string | null } | null;
  onChange: (v: { integration: string; account: string | null }) => void;
  /** Sync-argument choices keyed by account slug — see `useAccountCompanies`. */
  companyOf: Record<string, SyncArgChoice>;
}) {
  const eligible = options.filter((o) => o.features.some((f) => requiredFeatures.includes(f)));
  const accounts = eligible.find((o) => o.integration === value?.integration)?.accounts ?? [];
  // The hint belongs OUTSIDE the dropdown. The account select renders only when
  // there is more than one account, so a tenant with a single non-company
  // account would never see it there; and even with several, a menu item is
  // read only by someone who opens the menu. Whether the account about to be
  // synced can be walked at all is a fact about the run, so it is stated next
  // to it either way.
  const selected = value?.account ? companyOf[value.account] : undefined;
  const blocked = selected && selected.is_company === false ? selected : null;
  if (eligible.length === 0) return null;
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Integration"
          value={value?.integration ?? ''}
          onChange={(e) => onChange({ integration: e.target.value, account: null })}
          sx={{ minWidth: 180 }}
          disabled={eligible.length === 1}
        >
          {eligible.map((o) => (
            <MenuItem key={o.integration} value={o.integration}>
              {o.integration}
            </MenuItem>
          ))}
        </TextField>
        {accounts.length > 1 && (
          <TextField
            select
            size="small"
            label="Account"
            value={value?.account ?? ''}
            onChange={(e) => onChange({ integration: value!.integration, account: e.target.value || null })}
            sx={{ minWidth: 180 }}
          >
            {accounts.map((a) => (
              <MenuItem key={a.slug ?? '_default'} value={a.slug ?? ''}>
                {a.display_name}
                {a.slug && companyOf[a.slug]?.is_company === false && (
                  // Shown on the closed select too, since MUI renders the
                  // chosen item's children in the input.
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label="not a company"
                    sx={{ ml: 1 }}
                  />
                )}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      {blocked?.hint && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {blocked.hint}
        </Alert>
      )}
    </>
  );
}

/** Which of this integration's account slugs name a company the catalogue can
 *  be walked under.
 *
 *  `is_company` rides on the sync-argument choices and nowhere else — the item
 *  detail's account list carries a slug and a display name, neither of which
 *  can say whether the slug is a FareHarbor company shortname, which is what a
 *  `companies/<X>/` path segment is made of. Asked without an account, so the
 *  answer covers every slug at once; keyed by choice value, which under
 *  FareHarbor's account model IS the account slug.
 *
 *  Advisory: a failure (or an operator without the booking-sync permission)
 *  leaves the picker naming accounts exactly as it always did.
 */
function useAccountCompanies(topId: number, integration: string | null, enabled: boolean) {
  const q = useQuery({
    queryKey: ['sync-extra-args', topId, integration, null],
    queryFn: () =>
      syncApi.fetchSyncExtraArgs({ tour_operator_id: topId, integration: integration! }),
    enabled: enabled && Boolean(integration),
    retry: false,
  });
  return useMemo(() => {
    const out: Record<string, SyncArgChoice> = {};
    for (const arg of q.data?.extra_args ?? []) {
      for (const choice of arg.choices ?? []) {
        if (typeof choice.is_company === 'boolean') out[choice.value] = choice;
      }
    }
    return out;
  }, [q.data]);
}

interface Props {
  kind: 'catalog' | 'bookings';
  item: PreflightItem;
  top: TourOperatorDetail;
  topId: number;
  canManage: boolean;
  actions: PreflightItemActions;
}

export function DataSyncStep({ kind, item, top, topId, canManage, actions }: Props) {
  const snackbar = useSnackbar();
  const detail = (item.detail ?? {}) as SyncDetail;
  const options = detail.integration_options ?? [];
  const requiredFeatures = kind === 'catalog' ? ['product_sync'] : ['order_sync', 'booking_sync'];
  const launch = useItemLaunch(topId, item, actions);

  const [choice, setChoice] = useState<{ integration: string; account: string | null } | null>(null);
  const [logOpen, setLogOpen] = useState(false);
  const taskId = taskIdOf(item);
  const running = item.status === 'started';
  // The form collapses while a run is active or after completion; ops can
  // always reopen it for another run.
  const [formOpen, setFormOpen] = useState(!running && item.status !== 'completed');

  // Default the integration/account from the staged params, else the first
  // eligible configured option.
  useEffect(() => {
    if (choice) return;
    const staged = detail.sync_params ?? {};
    const eligible = options.filter((o) => o.features.some((f) => requiredFeatures.includes(f)));
    const stagedIntegration = typeof staged.integration_name === 'string' ? staged.integration_name : null;
    const fromStaged = eligible.find((o) => o.integration === stagedIntegration);
    const pick = fromStaged ?? eligible[0];
    if (pick) {
      const account =
        fromStaged && typeof staged.account === 'string'
          ? staged.account
          : (pick.accounts.find((a) => a.is_default) ?? pick.accounts[0])?.slug ?? null;
      setChoice({ integration: pick.integration, account });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.length]);

  const companyOf = useAccountCompanies(topId, choice?.integration ?? null, canManage);

  const scope: SyncScope = {
    tenant: { id: top.id, name: top.name },
    integration: choice?.integration ?? null,
    account: choice?.account ?? null,
  };
  // The same query (and cache entry) ProductSyncForm decides its catalogue mode with.
  const catalogueMode = Boolean(
    useCatalogueRegistry(kind === 'catalog' ? catalogueScopeOf(scope) : null).data,
  );

  const onLaunched = () => {
    snackbar.show(
      kind === 'catalog'
        ? 'Catalog import started — this item completes when the task finishes.'
        : 'Booking backfill started — this item completes when the task finishes.',
      'success',
    );
    setFormOpen(false);
    actions.invalidate();
  };

  const mode = detail.mode ?? 'bookings';
  const syncedCount = detail.synced_product_ids?.length ?? 0;

  // Missing inputs the embedded form cannot supply. `sync_params.*` keys ARE the
  // form (window, products, key); anything else — today the launch-policy gate
  // on the backfill — is a precondition the item refuses to run without, so it
  // is reported the way SetupItemPanel reports unanchored inputs and the form
  // stays closed until it clears (the server refuses `execute` either way).
  const unanchored =
    item.status === 'completed' ? [] : item.missing_inputs.filter((m) => !m.key.startsWith('sync_params'));
  const gated = unanchored.length > 0;

  return (
    <Box>
      {gated && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            unanchored.some((m) => m.key === 'launch_policy') ? (
              <Button
                size="small"
                color="inherit"
                component={RouterLink}
                to={`/tour-operators/${topId}/setup/run`}
              >
                Launch state
              </Button>
            ) : undefined
          }
        >
          Needs input: {unanchored.map((m) => `${m.label} (${m.why})`).join('; ')}
        </Alert>
      )}
      {item.status !== 'completed' && item.blocked_by.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Waiting on: {item.blocked_by.map((b) => b.action).join(', ')}. The staged run fires
          automatically once the prerequisite completes (armed setup) — or launch it here after.
        </Alert>
      )}
      {item.status === 'error' && item.error_message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {item.error_message}
        </Alert>
      )}
      {item.status === 'completed' && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            canManage ? (
              <Button size="small" onClick={() => setFormOpen((v) => !v)}>
                {formOpen ? 'Hide form' : kind === 'catalog' ? 'Sync more products' : 'Backfill again'}
              </Button>
            ) : undefined
          }
        >
          {kind === 'catalog'
            ? `${catalogueMode ? 'Catalogue synced' : 'Catalog imported'}${syncedCount ? ` — ${syncedCount} product(s)` : ''}.`
            : 'Booking backfill completed.'}
        </Alert>
      )}

      {taskId && (
        <Box sx={{ mb: 2 }}>
          <TaskProgressCard taskId={taskId} onTerminal={() => actions.invalidate()} />
        </Box>
      )}

      {gated && canManage ? (
        <Typography variant="body2" color="text.secondary">
          The {kind === 'catalog' ? 'import' : 'backfill'} form opens once the input above is
          resolved — the item refuses to start until then.
        </Typography>
      ) : options.length > 0 && canManage ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {kind === 'catalog'
                ? catalogueMode
                  ? 'Choose the catalogue products and launch the sync'
                  : 'Pick the products and launch the import'
                : `Backfill window (${mode} sync)`}
              {catalogueMode && <HelpLink story={CATALOGUE_STORY} newTab />}
            </Typography>
            <Tooltip title="View item log">
              <IconButton size="small" onClick={() => setLogOpen(true)} aria-label="View item log">
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {(running || item.status === 'completed') && (
              <IconButton
                size="small"
                onClick={() => setFormOpen((v) => !v)}
                aria-label={formOpen ? 'Hide form' : 'Show form'}
              >
                {formOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            )}
          </Box>
          <Collapse in={formOpen} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              {catalogueMode && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  The sync imports every included product (at most 500 per run), or only the rows
                  ticked in the grid. This step completes when the import task finishes; sync
                  again at any time, for instance after including more products.
                </Typography>
              )}
              <ScopeChoice
                options={options}
                requiredFeatures={requiredFeatures}
                value={choice}
                onChange={setChoice}
                companyOf={companyOf}
              />
              {choice && kind === 'catalog' && (
                <ProductSyncForm
                  scope={scope}
                  lockScope
                  onSubmit={(input: SyncProductsStartInput) =>
                    launch(input as unknown as Record<string, unknown>)
                  }
                  onLaunched={onLaunched}
                />
              )}
              {choice && kind === 'bookings' && mode === 'orders' && (
                <OrderSyncForm
                  scope={scope}
                  lockScope
                  onSubmit={(input: SyncOrdersStartInput) =>
                    launch({ ...(input as unknown as Record<string, unknown>), mode: 'orders' })
                  }
                  onLaunched={onLaunched}
                />
              )}
              {choice && kind === 'bookings' && mode === 'bookings' && (
                <BookingSyncForm
                  scope={scope}
                  lockScope
                  defaultSendSurveys={false}
                  onSubmit={(input: SyncBookingsStartInput) =>
                    launch({ ...(input as unknown as Record<string, unknown>), mode: 'bookings' })
                  }
                  onLaunched={onLaunched}
                />
              )}
            </Box>
          </Collapse>
        </>
      ) : !canManage ? (
        <Alert severity="info">
          Read-only access — the sync launch needs the preflight manage permission.
        </Alert>
      ) : (
        // A manager looking at a tenant with no configured integration used to get a
        // blank step body: the old ternary rendered `false` for this branch, so the
        // one case that needs an explanation showed nothing at all.
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              component={RouterLink}
              // The tab is a path segment (`/tour-operators/:id/:tab`), not a
              // query param — `?tab=integrations` landed on the General tab.
              to={`/tour-operators/${topId}/integrations`}
            >
              Configure integration
            </Button>
          }
        >
          This operator has no configured booking integration, so there is nothing to{' '}
          {kind === 'catalog' ? 'import from' : 'backfill from'} yet. Add one on the
          Integrations tab and come back — this step picks it up automatically.
        </Alert>
      )}

      <PreflightItemRunLogDrawer
        topId={topId}
        itemId={logOpen ? item.id : null}
        title={item.title}
        open={logOpen}
        onClose={() => setLogOpen(false)}
      />
    </Box>
  );
}
